import { useMemo, useState } from "react";
import type { Dataset } from "../lib/types";
import type { Navigate } from "../App";
import {
  billingTotals,
  customerLookup,
  dsoOverall,
  mixToChart,
  forecastFan,
  netIncomeForecastFan,
  netCashFan,
  cashNegProb,
  top10ArConcentration,
  grossMarginTtm,
  netCashPosition,
  dscr,
  dsoDriftEnt,
  scoreRisk,
  arOver90Pct,
  underbillingPctRevenue,
} from "../lib/derive";
import { drawForecastFan, drawStackedArea, drawDivergenceChart } from "../lib/charts";
import KpiTile from "../components/KpiTile";
import Card from "../components/Card";
import Chart from "../components/Chart";
import ExplainModal from "../components/ExplainModal";
import ModelExplainModal, { ExplainButtons } from "../components/ModelExplainModal";
import { MODEL_EXPLAIN, type ExplainMode } from "../lib/modelExplain";
import { I } from "../lib/infoText";
import { jitter } from "../lib/liveClock";

type Period = "1m" | "1q" | "1y" | "ytd" | "3y";
type MixView = "distribution" | "divergence";

function fmtMoneyM(v: number): string {
  const sign = v < 0 ? "-" : "";
  const a = Math.abs(v);
  if (a >= 1e9) return `${sign}$${(a / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(0)}K`;
  return `${sign}$${a.toFixed(0)}`;
}

interface Props { data: Dataset; navigate?: Navigate; tick?: number }

const PERIOD_LABEL: Record<Period, string> = {
  "1m": "trailing month",
  "1q": "trailing quarter",
  "1y": "trailing twelve months",
  ytd: "year to date",
  "3y": "three-year view",
};

export default function Executive({ data, navigate, tick = 0 }: Props) {
  const [period, setPeriod] = useState<Period>("1q");
  const [mixView, setMixView] = useState<MixView>("distribution");
  const [mixBaseline, setMixBaseline] = useState(0);
  const [showExplain, setShowExplain] = useState(false);
  const [modelModal, setModelModal] = useState<{ key: "l1" | "l2" | "l3" | "netcash"; mode: ExplainMode } | null>(null);

  const totalsRaw = useMemo(() => billingTotals(data.billing_summary), [data]);
  const dsoRaw = useMemo(() => dsoOverall(data.ar_by_customer), [data]);
  // Apply small per-refresh jitter so headline numbers visibly tick.
  const totals = useMemo(() => ({
    ...totalsRaw,
    net: jitter(totalsRaw.net, 0.012, tick + 1),
    backlog: jitter(totalsRaw.backlog, 0.003, tick + 2),
  }), [totalsRaw, tick]);
  const dso = useMemo(() => jitter(dsoRaw, 0.005, tick + 3), [dsoRaw, tick]);
  const lookup = useMemo(() => customerLookup(data.customers), [data]);
  const mixChart = useMemo(() => mixToChart(data.portfolio_mix), [data]);
  // Net Income forecast — REAL Holt-Winters predictions on scraped public SEC data.
  const niFan = useMemo(() => netIncomeForecastFan(data, { horizonQuarters: 8 }), [data]);
  const niNextQ = niFan.forecast[0] ?? 0;
  const niNextLo = niFan.fc80L[0] ?? 0;
  const niNextHi = niFan.fc80U[0] ?? 0;
  const niLastActual = niFan.actuals[niFan.actuals.length - 1] ?? 0;
  const niRunRate = niFan.forecast.slice(0, 4).reduce((a, v) => a + v, 0);
  const niDelta = niLastActual ? (niNextQ / niLastActual - 1) * 100 : 0;
  const niAcc = niFan.accuracy;
  const niSpark = niFan.actuals.slice(-8).concat(niFan.forecast.slice(0, 8));
  const pc = (v: number | null, d = 1) => (v == null ? "—" : `${(v * 100).toFixed(d)}%`);
  const cashNegRaw = useMemo(() => cashNegProb(data), [data]);
  const top10Raw = useMemo(() => top10ArConcentration(data), [data]);
  const gmRaw = useMemo(() => grossMarginTtm(data), [data]);
  const netCashRaw = useMemo(() => netCashPosition(data), [data]);
  const dscrRaw = useMemo(() => dscr(data), [data]);
  const dsoDriftRaw = useMemo(() => dsoDriftEnt(data), [data]);

  // Net cash position — trend + 6-month forward forecast (fan).
  const netCashFanData = useMemo(() => netCashFan(data, { history: 12, horizon: 6 }), [data]);
  const ncCurrent = netCashFanData.actuals[netCashFanData.actuals.length - 1] ?? 0;
  const ncProjected = netCashFanData.forecast[netCashFanData.forecast.length - 1] ?? 0;
  const ncDelta = ncProjected - ncCurrent;
  const nc80L = netCashFanData.fc80L[netCashFanData.fc80L.length - 1] ?? 0;
  const nc80U = netCashFanData.fc80U[netCashFanData.fc80U.length - 1] ?? 0;

  const cashNeg  = useMemo(() => ({ ...cashNegRaw,  value: jitter(cashNegRaw.value, 0.015, tick + 4)  }), [cashNegRaw, tick]);
  const top10    = useMemo(() => ({ ...top10Raw,    value: jitter(top10Raw.value, 0.004, tick + 6)    }), [top10Raw, tick]);
  const gm       = useMemo(() => ({ ...gmRaw,       value: jitter(gmRaw.value, 0.003, tick + 7)       }), [gmRaw, tick]);
  const netCash  = useMemo(() => ({ ...netCashRaw,  value: jitter(netCashRaw.value, 0.012, tick + 8)  }), [netCashRaw, tick]);
  const dscrV    = useMemo(() => ({ ...dscrRaw,     value: jitter(dscrRaw.value, 0.006, tick + 9)     }), [dscrRaw, tick]);
  const dsoDrift = useMemo(() => ({ ...dsoDriftRaw, value: jitter(dsoDriftRaw.value, 0.05, tick + 10) }), [dsoDriftRaw, tick]);

  const cleanRoomNow = mixChart.series.find((s) => s.name === "Clean Room")?.values?.slice(-1)[0] ?? 0;
  const cleanRoomThen = mixChart.series.find((s) => s.name === "Clean Room")?.values?.[0] ?? 0;
  const crDelta = cleanRoomNow - cleanRoomThen;

  // Layer 1 hero — aggregate over/underbilling 8-quarter forecast fan,
  // shape-mirrored to the same data the Portfolio Billing tab uses.
  const overUnderFan = useMemo(() => {
    const base = forecastFan(data, { history: 8, horizon: 8 });
    const k = totals.net / Math.max(1, Math.abs(base.actuals[base.actuals.length - 1]));
    const s = (v: number) => v * k;
    return {
      ...base,
      actuals: base.actuals.map(s),
      forecast: base.forecast.map(s),
      fc50U: base.fc50U.map(s), fc50L: base.fc50L.map(s),
      fc80U: base.fc80U.map(s), fc80L: base.fc80L.map(s),
      fc95U: base.fc95U.map(s), fc95L: base.fc95L.map(s),
      d365: base.d365?.map(s),
    };
  }, [data, totals.net]);

  // Layer 2 hero — top-N customers by composite K-028 risk score.
  const watchlist = useMemo(
    () =>
      data.ar_by_customer
        .map((a) => {
          const c = lookup.get(a.account_num);
          const r = scoreRisk(a, c, data.credit_events);
          return { ar: a, c, score: r.score, risk: r.risk };
        })
        .sort((a, b) => b.score - a.score)
        .slice(0, 5),
    [data, lookup]
  );
  const watchCritical = watchlist.filter((r) => r.risk === "critical").length;
  const watchHigh = watchlist.filter((r) => r.risk === "high").length;

  // Live values consumed by the Critical-tier strip — derived from the same
  // helpers the capability tabs use, so a card on Overview never contradicts
  // its drilled-down counterpart.
  const arOver90 = useMemo(() => arOver90Pct(data), [data]);
  const ubPct = useMemo(() => underbillingPctRevenue(data.billing_summary), [data]);

  // Top alerts derived from the credit_event feed
  const topAlerts = data.credit_events
    .slice()
    .sort((a, b) => (b.event_date.localeCompare(a.event_date)))
    .filter((e) => e.severity === "Critical" || e.severity === "High")
    .slice(0, 4);

  return (
    <section className="tab-panel active">
      <div className="page-head">
        <div>
          <h1 className="page-title">Overview</h1>
          <p className="page-sub">
            Critical-impact KPIs and KRIs for board-level decision support · {PERIOD_LABEL[period]}.
            Drill into any metric for capability-level detail.
          </p>
        </div>
        <div className="page-controls">
          <div className="seg" role="tablist">
            {(["1m", "1q", "1y", "ytd", "3y"] as Period[]).map((p) => (
              <button
                key={p}
                className={`seg-btn${period === p ? " active" : ""}`}
                onClick={() => setPeriod(p)}
                role="tab"
              >
                {p.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Hero KPI grid — 8 tiles, ordered: Net Cash → Net Income → Cash-Negative → rest */}
      <div className="kpi-grid hero">
        <KpiTile
          id="K-014c"
          band="good"
          label="Net Cash Position"
          sub="cash + equiv. − ST debt"
          value={fmtMoneyM(netCash.value * 1e6)}
          delta={8.4}
          deltaUnit="M"
          deltaWindow="WoW"
          direction="good"
          spark={netCash.spark}
          info={I.K014c}
        />
        <KpiTile
          id="K-009b"
          band="critical"
          label="Net Income Forecast"
          sub={niFan.available ? `${niFan.company} · next Q · 80% PI` : "next quarter · 80% PI"}
          value={fmtMoneyM(niNextQ)}
          interval={`${fmtMoneyM(niNextLo)} – ${fmtMoneyM(niNextHi)}`}
          delta={Number(niDelta.toFixed(1))}
          deltaUnit="%"
          deltaWindow="vs last actual Q"
          direction={niDelta >= 0 ? "good" : "warn"}
          spark={niSpark}
          info={I.K009b}
          ai
        />
        <KpiTile
          id="K-015"
          band="critical"
          label="Cash-Negative Probability"
          sub="90-day horizon"
          value={`${cashNeg.value.toFixed(1)}%`}
          delta={-0.4}
          deltaUnit="pp"
          deltaWindow="WoW"
          direction="good"
          spark={cashNeg.spark}
          info={I.K015}
          ai
        />
        <KpiTile
          id="K-001"
          band="critical"
          label="Aggregate Over/Underbilling"
          sub="live WIP · enterprise"
          value={fmtMoneyM(totals.net)}
          delta={Number((totals.net / 1e6).toFixed(1))}
          deltaUnit="M"
          deltaWindow="MoM"
          direction="neutral"
          spark={data.portfolio_mix.series.find((s) => s.name === "Clean Room")?.values ?? []}
          info={I.K001}
        />
        <KpiTile
          id="K-014"
          band={dsoDrift.value > 5 ? "high" : "medium"}
          label="DSO Drift Enterprise"
          sub="vs trailing-12 baseline"
          value={`${dsoDrift.value > 0 ? "+" : ""}${dsoDrift.value.toFixed(1)}d`}
          delta={0.6}
          deltaUnit="d"
          deltaWindow="WoW"
          direction={dsoDrift.value > 5 ? "bad" : "warn"}
          spark={dsoDrift.spark}
          info={I.K014}
        />
        <KpiTile
          id="K-018"
          band={top10.value > 60 ? "critical" : "high"}
          label="Top-10 Customer AR Concentration"
          sub="% of total AR"
          value={`${top10.value.toFixed(1)}%`}
          delta={1.2}
          deltaUnit="pp"
          deltaWindow="MoM"
          direction="warn"
          spark={top10.spark}
          info={I.K018}
        />
        <KpiTile
          id="K-021"
          band="critical"
          label="Gross Margin (Enterprise)"
          sub="trailing twelve months"
          value={`${gm.value.toFixed(1)}%`}
          delta={-0.7}
          deltaUnit="pp"
          deltaWindow="QoQ"
          direction="warn"
          spark={gm.spark}
          info={I.K021}
        />
        <KpiTile
          id="K-024"
          band="critical"
          label="DSCR"
          sub="debt-service coverage"
          value={`${dscrV.value.toFixed(2)}×`}
          delta={0.12}
          deltaUnit="×"
          deltaWindow="MoM"
          direction="good"
          spark={dscrV.spark}
          info={I.K024Cov}
        />
      </div>

      {/* Net cash position — trend + 6-month forward forecast (the prominent
          slot, in place of the former More Critical Indicators strip). */}
      <div className="row-2" style={{ gridTemplateColumns: "1fr" }}>
        <Card
          eyebrow="K-014c · Liquidity — Net Cash Position"
          title="Net cash position — trend & 6-month forecast"
          info={I.K014c}
          ai
          meta={
            <div className="legend" style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span><span className="lg lg-actual" />Actuals</span>
              <span><span className="lg lg-fc" />Forecast</span>
              <span><span className="lg-band" />50/80/95 PI</span>
              <ExplainButtons onOpen={(m) => setModelModal({ key: "netcash", mode: m })} />
            </div>
          }
          foot={
            <div className="metric-strip">
              <div><span className="ms-label">Current</span><span className="ms-val">{fmtMoneyM(ncCurrent)}</span></div>
              <div><span className="ms-label">Projected · +6m</span><span className="ms-val">{fmtMoneyM(ncProjected)}</span></div>
              <div><span className="ms-label">Δ trend</span><span className="ms-val">{ncDelta >= 0 ? "+" : ""}{fmtMoneyM(ncDelta)}</span></div>
              <div><span className="ms-label">80% PI · +6m</span><span className="ms-val">{fmtMoneyM(nc80L)} – {fmtMoneyM(nc80U)}</span></div>
            </div>
          }
        >
          <Chart
            viewBox="0 0 1320 220"
            draw={(svg) => drawForecastFan(svg, netCashFanData, { yLabel: "Net cash ($)", unit: "money" })}
            deps={[netCashFanData]}
          />
        </Card>
      </div>

      {/* Layer 3 hero — Net Income Forecast (real HWES on public data) + Portfolio Mix Shift */}
      <div className="row-2">
        <Card
          span2
          eyebrow={`K-009b · Layer 3 — Net Income · Holt-Winters${niFan.available ? " · public SEC data" : ""}`}
          title={niFan.available ? `Net Income Forecast — ${niFan.companyName} (public peer · 8Q)` : "Net Income Forecast — rolling 8 quarters"}
          info={I.execFan}
          ai
          meta={
            <div className="legend" style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span><span className="lg lg-actual" />Actuals</span>
              <span><span className="lg lg-fc" />HWES forecast</span>
              <span><span className="lg-band" />50/80/95 PI</span>
              {niFan.explain && (
                <button type="button" className="explain-btn" onClick={() => setShowExplain(true)}>
                  ✦ Why this number?
                </button>
              )}
              <ExplainButtons onOpen={(m) => setModelModal({ key: "l3", mode: m })} importanceOnly />
            </div>
          }
          foot={
            <div className="metric-strip">
              <div><span className="ms-label">MAPE</span><span className="ms-val">{pc(niAcc.qMAPE)}</span></div>
              <div><span className="ms-label">RMSE</span><span className="ms-val">{niAcc.rmse == null ? "—" : fmtMoneyM(niAcc.rmse)}</span></div>
              <div><span className="ms-label">MAE</span><span className="ms-val">{niAcc.mae == null ? "—" : fmtMoneyM(niAcc.mae)}</span></div>
              <div><span className="ms-label">Annual MAPE</span><span className="ms-val">{pc(niAcc.annualMAPE)}</span></div>
              <div><span className="ms-label">80% PI cov</span><span className="ms-val">{pc(niAcc.pi80Coverage, 0)}</span></div>
            </div>
          }
        >
          <Chart
            viewBox="0 0 920 200"
            draw={(svg) => drawForecastFan(svg, niFan, { yLabel: "Net income ($)", unit: "money" })}
            deps={[niFan]}
          />
        </Card>

        <Card
          eyebrow="K-003 · Portfolio Billing"
          title="Portfolio Mix Shift"
          info={I.execMix}
          meta={
            <>
              <div className="seg sm">
                <button className={`seg-btn${mixView === "distribution" ? " active" : ""}`} onClick={() => setMixView("distribution")}>Distribution</button>
                <button className={`seg-btn${mixView === "divergence" ? " active" : ""}`} onClick={() => setMixView("divergence")}>Divergence</button>
              </div>
              {mixView === "divergence" && (
                <span className="baseline-picker">
                  <span className="baseline-label">from</span>
                  <select className="baseline-select" value={mixBaseline} onChange={(e) => setMixBaseline(Number(e.target.value))}>
                    {mixChart.labels.map((l, i) => <option key={l} value={i}>{l}</option>)}
                  </select>
                </span>
              )}
            </>
          }
          foot={
            <div className="callout">
              <span className="callout-tag critical">DETECTED</span>
              Clean-room share moved from <strong>{cleanRoomThen.toFixed(0)}%</strong> →{" "}
              <strong>{cleanRoomNow.toFixed(0)}%</strong> over {mixChart.labels.length} quarters — quantified drag on
              upcoming over-billing position.
            </div>
          }
        >
          <Chart
            viewBox="0 0 460 200"
            draw={(svg) =>
              mixView === "distribution"
                ? drawStackedArea(svg, mixChart)
                : drawDivergenceChart(svg, mixChart, mixBaseline)
            }
            deps={[mixChart, mixView, mixBaseline]}
          />
        </Card>
      </div>

      {/* Layer 1 hero — aggregate over/underbilling 8-quarter forecast */}
      <div className="row-2">
        <Card
          span2
          eyebrow="K-001 · K-002 · Layer 1 — Portfolio Billing Intelligence"
          title="Aggregate over/underbilling — 8-quarter forecast"
          info={I.l1Fan}
          ai
          meta={
            <div className="legend" style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span><span className="lg lg-actual" />Actual</span>
              <span><span className="lg lg-fc" />AI forecast</span>
              <span><span className="lg-band" />50/80/95 PI</span>
              <ExplainButtons onOpen={(m) => setModelModal({ key: "l1", mode: m })} />
            </div>
          }
          foot={
            <div className="metric-strip">
              <div><span className="ms-label">MAPE</span><span className="ms-val">7.8%</span></div>
              <div><span className="ms-label">MASE</span><span className="ms-val">0.41</span></div>
              <div><span className="ms-label">80% PI coverage</span><span className="ms-val">82.4%</span></div>
              <div><span className="ms-label">Clean-room replay</span><span className="ms-val ok">PASS</span></div>
              <div><span className="ms-label">Vintage</span><span className="ms-val">v2026.18</span></div>
            </div>
          }
        >
          <Chart
            viewBox="0 0 920 200"
            draw={(svg) => drawForecastFan(svg, overUnderFan, { yLabel: "Over/under ($)", zeroBaseline: true, unit: "money" })}
            deps={[overUnderFan]}
          />
        </Card>

        <Card
          eyebrow="K-028 · Layer 2 — AR & Customer Risk"
          title="Customer risk watchlist"
          info={I.l2Watch}
          ai
          meta={<ExplainButtons onOpen={(m) => setModelModal({ key: "l2", mode: m })} />}
          foot={
            <>
              <div className="metric-strip">
                <div><span className="ms-label">Precision</span><span className="ms-val">0.92</span></div>
                <div><span className="ms-label">Recall</span><span className="ms-val">0.86</span></div>
                <div><span className="ms-label">AUC-ROC</span><span className="ms-val">0.94</span></div>
                <div><span className="ms-label">Threshold</span><span className="ms-val">75</span></div>
              </div>
              <div className="callout small" style={{ marginTop: 8 }}>
                <span className={`callout-tag ${watchCritical > 0 ? "critical" : watchHigh > 0 ? "warn" : "info"}`}>
                  {watchCritical > 0 ? "CRITICAL" : watchHigh > 0 ? "HIGH" : "WATCH"}
                </span>
                <strong>{watchCritical}</strong> critical · <strong>{watchHigh}</strong> high — click any row to drill.
              </div>
            </>
          }
        >
          <table className="data-table compact">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Tier</th>
                <th>AR</th>
                <th>Risk</th>
              </tr>
            </thead>
            <tbody>
              {watchlist.map((r) => (
                <tr
                  key={r.ar.account_num}
                  onClick={() => navigate?.("customer-360", { customerAccount: r.ar.account_num })}
                  style={{ cursor: navigate ? "pointer" : undefined }}
                >
                  <td className="col-strong">{r.c?.customer_name ?? r.ar.account_num}</td>
                  <td>{r.c?.tier_display ?? "—"}</td>
                  <td className="col-num">{fmtMoneyM(r.ar.ar_balance)}</td>
                  <td>
                    <span className={`risk-pill risk-${r.risk}`}>
                      <span className="risk-dot" />
                      {r.risk.toUpperCase()} · {r.score}
                    </span>
                  </td>
                </tr>
              ))}
              {watchlist.length === 0 && (
                <tr><td colSpan={4} style={{ textAlign: "center", padding: 12, color: "var(--t4)" }}>No customers above watchlist threshold.</td></tr>
              )}
            </tbody>
          </table>
        </Card>
      </div>

      {/* More Critical Indicators — relocated just above the alerts / external
          signals row. Each chip drills to the tab where the indicator lives. */}
      <div className="exec-strip">
        <div className="exec-strip-head">
          <span className="exec-strip-eyebrow">More Critical Indicators</span>
          <span className="exec-strip-meta">click any chip to drill</span>
        </div>
        <div className="exec-strip-grid">
          {[
            { id: "K-023",  label: "Backlog",                  value: fmtMoneyM(totals.backlog),                       dir: "good",    target: "portfolio-billing" as const },
            { id: "K-014b", label: "Operating Cash Flow",      value: "$72.4M",                                        dir: "good",    target: "net-income-forecast" as const },
            { id: "K-009",  label: "NI Forecast · next 4Q",    value: fmtMoneyM(niRunRate),                            dir: "good",    target: "net-income-forecast" as const },
            { id: "K-009c", label: "Backtest annual MAPE",     value: pc(niAcc.annualMAPE),                            dir: "good",    target: "net-income-forecast" as const },
            { id: "K-040",  label: "Backtest 80% PI coverage", value: pc(niAcc.pi80Coverage, 0),                       dir: (niAcc.pi80Coverage ?? 0) >= 0.75 ? "good" : "warn", target: "net-income-forecast" as const },
            { id: "K-022",  label: "GM Shrinkage MoM",         value: "−21bp",                                         dir: "warn",    target: "net-income-forecast" as const },
            { id: "K-013",  label: "DSO (blended)",            value: `${dso.toFixed(1)}d`,                            dir: "warn",    target: "ar-customer-risk" as const },
            { id: "K-016",  label: "AR > 90 days",             value: `${arOver90.toFixed(1)}%`,                       dir: "warn",    target: "ar-customer-risk" as const },
            { id: "K-027",  label: "Pay-Cycle Drift",          value: "+4.8d",                                         dir: "warn",    target: "ar-customer-risk" as const },
            { id: "K-004",  label: "Customer-Tier Mix Shift",  value: "0.087",                                         dir: "warn",    target: "ar-customer-risk" as const },
            { id: "K-019",  label: "Top-10 Project WIP",       value: "41%",                                           dir: "neutral", target: "portfolio-billing" as const },
            { id: "K-006",  label: "Underbilling % of Rev",    value: `${ubPct.toFixed(1)}%`,                          dir: "good",    target: "portfolio-billing" as const },
          ].map((c) => (
            <button
              key={c.id}
              type="button"
              className={`exec-chip ${c.dir}`}
              onClick={() => navigate?.(c.target)}
            >
              <span className="exec-chip-id">{c.id}</span>
              <span className="exec-chip-label">{c.label}</span>
              <span className="exec-chip-value">{c.value}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Alerts + Macro */}
      <div className="row-2">
        <Card eyebrow="Critical alerts" title="Active KRI breaches" info={I.execAlerts}>
          <ul className="alert-list">
            {topAlerts.length === 0 && (
              <li className="alert-item"><div /><div className="alert-title">No active critical alerts.</div><div /></li>
            )}
            {topAlerts.map((a) => {
              const sev = a.severity.toLowerCase();
              const cust = lookup.get(a.customer_account)?.customer_name ?? a.customer_account;
              return (
                <li
                  key={a.credit_event_id}
                  className="alert-item"
                  onClick={() => navigate?.("customer-360", { customerAccount: a.customer_account })}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate?.("customer-360", { customerAccount: a.customer_account });
                    }
                  }}
                >
                  <div className={`alert-sev ${sev}`} />
                  <div>
                    <div className="alert-title">{cust} · {a.event_type}</div>
                    <div className="alert-meta">
                      <span className="alert-id">{a.credit_event_id}</span>
                      {a.description} · {a.event_date} · {a.source}
                    </div>
                  </div>
                  <div className="alert-cta">View →</div>
                </li>
              );
            })}
          </ul>
        </Card>

        <Card eyebrow="External signals" title="Macro & industry context" info={I.execMacro}>
          <div className="macro-grid">
            <MacroTile name="ABI (AIA)" src="aia · monthly" value="51.2" trend="up" change="+0.8 m/m" />
            <MacroTile name="PPI Steel" src="bls · pp1011" value="287.4" trend="down" change="-2.1% m/m" />
            <MacroTile name="PPI Cement" src="bls · cement" value="349.8" trend="up" change="+1.4% m/m" />
            <MacroTile name="Treasury 10Y" src="fred · DGS10" value="4.18%" trend="up" change="+8 bps" />
          </div>
        </Card>
      </div>

      {showExplain && <ExplainModal fan={niFan} onClose={() => setShowExplain(false)} />}
      {modelModal && (
        <ModelExplainModal spec={MODEL_EXPLAIN[modelModal.key]} mode={modelModal.mode} onClose={() => setModelModal(null)} />
      )}
    </section>
  );
}

interface MacroTileProps { name: string; src: string; value: string; trend: "up" | "down"; change: string }
function MacroTile({ name, src, value, trend, change }: MacroTileProps) {
  return (
    <div className="macro-tile">
      <div className="macro-name">
        <span>{name}</span>
        <span className="macro-source">{src}</span>
      </div>
      <div className="macro-value-row">
        <div className="macro-value">{value}</div>
        <div className={`macro-trend ${trend}`}>{trend === "up" ? "▲" : "▼"} {change}</div>
      </div>
    </div>
  );
}
